import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useSafeFetch } from "../../lib/useSafeFetch";
import { useConfirm } from "../../components/ConfirmDialog";
import ActivityFeedPanel from "../../components/admin/ActivityFeedPanel";
import {
  Users, Plus, Pencil, Trash2, ShieldCheck, X, Save, Loader2, Eye, EyeOff,
  CheckCircle2, Ban,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { roleLabel, roleTintClass, isSysAdmin } from "../../lib/roles";
import { toast } from "sonner";
import { MENU_ITEMS, emptyMenuAcl } from "../../lib/menuAcl";

// v4.6.8 — the permission matrix mirrors the admin sidebar 1-to-1.
const ACL_SECTIONS = MENU_ITEMS.map((m) => ({ id: m.slug, label: m.label, actions: m.actions }));

const ROLE_OPTIONS = [
  { value: "super_admin", label: "SysAdmin" },
  { value: "admin",       label: "Admin" },
  { value: "editor",      label: "Staff / Editor" },
];

const EMPTY_ACL = emptyMenuAcl();

export default function AdminUsers() {
  const { user } = useAuth();
  const [modal, setModal] = useState(null);  // {mode:"create"|"edit", user?}
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: users, refetch, setData: setUsers, loading } = useSafeFetch(
    "/admin/users",
    { defaultValue: [], asList: true, enabled: !!user && isSysAdmin(user) },
  );
  const { confirm, dialog: confirmDialog } = useConfirm();

  if (user && !isSysAdmin(user)) return <Navigate to="/admin" replace />;

  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (statusFilter === "active"   && u.is_active === false) return false;
    if (statusFilter === "inactive" && u.is_active !== false) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const remove = async (u) => {
    if (u.id === user?.id) {
      toast.error("You cannot delete your own account.");
      return;
    }
    const ok = await confirm({
      title:        "Delete user",
      message:      "The user loses access immediately. Their historical booking + audit trail entries remain intact.",
      subject:      `${u.email} · ${roleLabel(u.role)}`,
      confirmLabel: "Delete user",
      testId:       "confirm-delete-user",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("User deleted");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const toggleActive = async (u) => {
    if (u.id === user?.id) {
      toast.error("You cannot deactivate your own account.");
      return;
    }
    try {
      const { data } = await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      setUsers((prev) => prev.map((x) => x.id === u.id ? data : x));
      toast.success(data.is_active ? "User activated" : "User deactivated");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Access Control</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            User <em className="italic">management</em>.
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
            Create staff accounts, tune granular section permissions, or deactivate access.
            SysAdmin has unconditional full access.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ mode: "create" })}
          className="bg-black text-white dark:bg-white dark:text-black px-5 py-2.5 text-sm inline-flex items-center gap-2 rounded"
          data-testid="new-user-btn"
        >
          <Plus size={14} /> Add new user
        </button>
      </div>

      {loading && <p className="text-neutral-500 dark:text-neutral-400">Loading…</p>}

      {/* Search + filter toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="users-toolbar">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email or phone…"
          data-testid="users-search"
          className="flex-1 min-w-[260px] bg-white dark:bg-[#1F2937] border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm focus:outline-none focus:border-black dark:focus:border-white"
        />
        <div className="flex items-center gap-1.5" data-testid="users-role-chips">
          {[
            { v: "all",         label: "All roles" },
            { v: "super_admin", label: "SysAdmin" },
            { v: "admin",       label: "Admin" },
            { v: "editor",      label: "Editor" },
          ].map((c) => (
            <button
              key={c.v}
              type="button"
              onClick={() => setRoleFilter(c.v)}
              data-testid={`filter-role-${c.v}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                roleFilter === c.v
                  ? "bg-black text-white dark:bg-white dark:text-black border-black dark:border-white"
                  : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5" data-testid="users-status-chips">
          {[
            { v: "all",      label: "All status" },
            { v: "active",   label: "Active" },
            { v: "inactive", label: "Inactive" },
          ].map((c) => (
            <button
              key={c.v}
              type="button"
              onClick={() => setStatusFilter(c.v)}
              data-testid={`filter-status-${c.v}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                statusFilter === c.v
                  ? "bg-black text-white dark:bg-white dark:text-black border-black dark:border-white"
                  : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 ml-auto" data-testid="users-count">
          {filtered.length}/{users.length}
        </p>
      </div>

      <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 bg-[#F3F4F6] dark:bg-[#111827]">
            <tr>
              <th className="py-3 px-5 font-medium">Name</th>
              <th className="py-3 px-4 font-medium">Email</th>
              <th className="py-3 px-4 font-medium">Phone</th>
              <th className="py-3 px-4 font-medium">Role</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-black/5 dark:border-white/5" data-testid={`user-row-${u.id}`}>
                <td className="py-3 px-5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center">
                      <Users size={13} className="text-neutral-500" />
                    </div>
                    <span className="font-medium">{u.name}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-neutral-600 dark:text-neutral-300">{u.email}</td>
                <td className="py-3 px-4 text-neutral-500 dark:text-neutral-400 tabular-nums">{u.phone || "—"}</td>
                <td className="py-3 px-4">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${roleTintClass(u.role)}`}>
                    {roleLabel(u.role)}
                  </span>
                </td>
                <td className="py-3 px-4">
                  {u.is_active !== false ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400" data-testid={`status-${u.id}`}>
                      <CheckCircle2 size={13} /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-neutral-500" data-testid={`status-${u.id}`}>
                      <Ban size={13} /> Inactive
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setModal({ mode: "edit", user: u })}
                    className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded inline-flex"
                    title="Edit user"
                    data-testid={`edit-${u.id}`}
                  >
                    <Pencil size={13} />
                  </button>
                  {u.role !== "super_admin" && (
                    <button
                      type="button"
                      onClick={() => toggleActive(u)}
                      className="ml-1 p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded inline-flex"
                      title={u.is_active === false ? "Activate" : "Deactivate"}
                      data-testid={`toggle-${u.id}`}
                    >
                      {u.is_active === false ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Ban size={13} className="text-neutral-500" />}
                    </button>
                  )}
                  {u.role !== "super_admin" && (
                    <button
                      type="button"
                      onClick={() => remove(u)}
                      className="ml-1 p-1.5 hover:bg-rose-100 dark:hover:bg-rose-950/30 rounded inline-flex text-rose-600 dark:text-rose-400"
                      title="Delete user"
                      data-testid={`delete-${u.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                  {u.role === "super_admin" && (
                    <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400" title="SysAdmin cannot be deleted from the UI">
                      <ShieldCheck size={12} />
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="py-16 text-center text-neutral-500 dark:text-neutral-400">
                  {users.length === 0
                    ? 'No staff accounts yet — click "Add new user" to create the first one.'
                    : "No users match the current filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <UserModal
          mode={modal.mode}
          initial={modal.user}
          currentUserId={user?.id}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setUsers((prev) => {
              const idx = prev.findIndex((x) => x.id === saved.id);
              return idx === -1 ? [saved, ...prev] : prev.map((x) => x.id === saved.id ? saved : x);
            });
            setModal(null);
            refetch();
          }}
        />
      )}
      {confirmDialog}

      {/* Activity feed — live audit-log tail. Moved here from the main
          Admin Dashboard per iter-48 spec so SysAdmins can spot who
          created / edited / disabled which staff account. */}
      <div className="mt-8" data-testid="users-activity-feed">
        <ActivityFeedPanel />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Create / Edit modal
// ─────────────────────────────────────────────────────────────────────
function UserModal({ mode, initial, currentUserId, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => ({
    name:      initial?.name || "",
    email:     initial?.email || "",
    phone:     initial?.phone || "",
    role:      initial?.role || "editor",
    is_active: initial?.is_active !== false,
    password:  "",
    acl:       { ...EMPTY_ACL, ...(initial?.acl || {}) },
  }));
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isSuper = form.role === "super_admin";
  const isSelf  = isEdit && initial?.id === currentUserId;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePerm = (section, action) => {
    setForm((f) => {
      const acl = { ...f.acl };
      const sec = { ...(acl[section] || {}) };
      sec[action] = !sec[action];
      acl[section] = sec;
      return { ...f, acl };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) { setError("Full name is required"); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { setError("Valid email is required"); return; }
    if (!isEdit && form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (isEdit && form.password && form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      const payload = {
        name:      form.name.trim(),
        email:     form.email.trim().toLowerCase(),
        phone:     form.phone.trim(),
        role:      form.role,
        is_active: form.is_active,
        acl:       isSuper ? undefined : form.acl,
      };
      if (form.password) payload.password = form.password;
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/admin/users/${initial.id}`, payload);
        saved = data;
        toast.success("User updated");
      } else {
        const { data } = await api.post("/admin/users", payload);
        saved = data;
        toast.success("User created");
      }
      onSaved(saved);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="user-modal">
      <form
        onSubmit={submit}
        className="bg-white dark:bg-[#1F2937] w-full max-w-2xl max-h-[90vh] overflow-auto rounded-lg shadow-xl"
      >
        <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
              {isEdit ? "Edit" : "Create"}
            </p>
            <h2 className="font-display text-xl mt-1">
              {isEdit ? `${initial?.name || "user"}` : "New staff account"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded" data-testid="user-modal-close">
            <X size={16} />
          </button>
        </header>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name" required>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} testId="um-name" />
            </Field>
            <Field label="Email (login)" required>
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} testId="um-email" />
            </Field>
            <Field label="Phone">
              <Input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} testId="um-phone" />
            </Field>
            <Field label={isEdit ? "New password (leave blank to keep)" : "Password"} required={!isEdit}>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                  data-testid="um-password"
                  autoComplete="new-password"
                  className="w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 pr-10 text-sm focus:outline-none focus:border-black dark:focus:border-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black dark:hover:text-white p-1"
                  data-testid="um-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </Field>
            <Field label="Role" required>
              <Select value={form.role} onChange={(e) => update("role", e.target.value)} testId="um-role">
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Select>
            </Field>
            <Field label="Status">
              <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => update("is_active", e.target.checked)}
                  disabled={isSelf}
                  data-testid="um-active"
                  className="w-4 h-4 accent-black dark:accent-white"
                />
                {form.is_active ? "Active" : "Inactive"}
                {isSelf && <span className="text-[11px] text-neutral-500">(cannot deactivate yourself)</span>}
              </label>
            </Field>
          </div>

          {/* ACL grid — hidden for SysAdmin (full access always) */}
          {!isSuper && (
            <div className="pt-2" data-testid="um-acl">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-3">
                Granular permissions
              </p>
              <div className="border border-black/10 dark:border-white/10 rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 bg-[#F3F4F6] dark:bg-[#111827]">
                    <tr>
                      <th className="py-2 px-4 font-medium">Menu item</th>
                      {["view", "create", "edit", "delete"].map((a) => (
                        <th key={a} className="py-2 px-3 font-medium text-center capitalize">{a}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ACL_SECTIONS.map((s) => (
                      <tr key={s.id} className="border-t border-black/5 dark:border-white/5">
                        <td className="py-2 px-4">{s.label}</td>
                        {["view", "create", "edit", "delete"].map((a) => (
                          <td key={a} className="py-2 px-3 text-center">
                            {s.actions.includes(a) ? (
                              <input
                                type="checkbox"
                                checked={!!(form.acl?.[s.id]?.[a])}
                                onChange={() => togglePerm(s.id, a)}
                                data-testid={`um-acl-${s.id}-${a}`}
                                className="w-4 h-4 accent-black dark:accent-white"
                              />
                            ) : (
                              <span className="text-neutral-300 dark:text-neutral-600">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {isSuper && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1" data-testid="um-full-access">
              <ShieldCheck size={13} /> SysAdmin has unconditional full access — permission grid disabled.
            </p>
          )}

          {error && <p className="text-red-600 dark:text-rose-400 text-sm" data-testid="um-error">{error}</p>}
        </div>

        <footer className="px-6 py-4 border-t border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2" data-testid="um-cancel">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            className="bg-black text-white dark:bg-white dark:text-black text-sm px-5 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="um-submit"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {isEdit ? "Save changes" : "Create user"}
          </button>
        </footer>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tiny form primitives (kept local to avoid an extra file)
// ─────────────────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <label>
      <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-2">
        {label}{required && <span className="text-rose-500 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
function Input(props) {
  const { testId, ...rest } = props;
  return (
    <input
      data-testid={testId}
      {...rest}
      className="w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm focus:outline-none focus:border-black dark:focus:border-white"
    />
  );
}
function Select({ testId, children, ...rest }) {
  return (
    <select
      data-testid={testId}
      {...rest}
      className="w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm focus:outline-none focus:border-black dark:focus:border-white"
    >
      {children}
    </select>
  );
}
